import React, { useState } from "react";
import { FormControl, FormGroup, FormLabel } from "react-bootstrap";
import Layout from "components/Layout";
import SubmitButton from "components/SubmitButton";
import { workflowsService } from "services";
import { errorFormating } from "utils/utils";
import Switch from "components/Switch";
import { useTranslation } from "react-i18next";

function AbsenceImports() {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [importErrors, setImportErrors] = useState([]);
	const [importMessages, setImportMessages] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const [fileInputKey, setFileInputKey] = useState(0);
	const [ignoreErrors, setIgnoreErrors] = useState(false);

	const resetFileInput = () => {
		setSelectedFile(null);
		setFileInputKey((key) => key + 1);
	};

	const handleSwitch = (event) => {
		setIgnoreErrors(event.target.checked);
	};

	const handleDownloadTemplate = async () => {
		const response = await workflowsService.downloadImportTemplate();
		const url = window.URL.createObjectURL(response);
		const a = document.createElement("a");
		document.body.appendChild(a);
		a.href = url;
		a.download = "import_template.xlsx";
		a.click();
	};

	const handleResponse = (response) => {
		const newErrors = [];

		if (Array.isArray(response.rows) && response.rows.length > 0) {
			const rowErrors = response.rows.filter(
				(row) => Array.isArray(row.errors) && row.errors.length > 0
			);
			newErrors.push(...rowErrors);
		}

		if (response.header && Array.isArray(response.header.errors)) {
			const headerErrors = response.header.errors.filter(
				(error) => error && error.message
			);
			newErrors.push(...headerErrors);
		}

		setIsLoading(false);
		setErrors(response.ok ? {} : { message: t("absences.import.errorGeneric") });
		setImportMessages([]);
		setImportErrors(newErrors);
	};

	const runImport = async (token) => {
		setIsLoading(true);

		try {
			const response = await workflowsService.runImport(token);

			if (response.ok) {
				setIsLoading(false);
				setSelectedFile(null);
				setFileInputKey((key) => key + 1);
				setErrors({});
				setImportErrors(response.has_errors && response.rows.length > 0 ? response.rows : []);
				setImportMessages(response.rows.length > 0 ? response.rows : []);
			} else {
				setIsLoading(false);
				setErrors(errorFormating(response));
			}
		} catch (error) {
			console.error("Error during import:", error);
			resetFileInput();
			setIsLoading(false);
			setImportMessages([]);
			setImportErrors([]);
			setErrors({ message: t("absences.import.errorGeneric") });
		}
	};

	const handleImport = async (event) => {
		event.preventDefault();
		setIsLoading(true);

		if (selectedFile) {
			try {
				const formData = new FormData();
				formData.append("filename", selectedFile);
				const response = await workflowsService.prepareImport(formData);

				if (ignoreErrors) {
					runImport(response.token);
				} else {
					handleResponse(response);
				}
			} catch (error) {
				console.error("Error during import:", error);
				resetFileInput();
				setIsLoading(false);
				setImportMessages([]);
				setImportErrors([]);
				setErrors({ message: t("absences.import.errorGeneric") });
			}
		} else {
			setIsLoading(false);
			setImportMessages([]);
			setImportErrors([]);
			setErrors({ message: t("absences.import.errorNoFile") });
		}
	};

	const getImportErrors = (rows = [], headerErrors = []) => {
		const rowErrors = rows.map((row, i) => {
			const hasErrors = row.errors && row.errors.length > 0;
			if (!hasErrors) {
				return row.message ? (
					<li key={`row-${i}`}>
						<span className="import-count">{row.message}</span>
					</li>
				) : null;
			}
			return (
				<li key={`row-${i}`}>
					<span className="import-count">{t("absences.import.row")} {row.row}</span>
					<ul className="import-details">
						{(row.errors || []).map((error, j) => (
							<li key={j}>{error.message}</li>
						))}
					</ul>
				</li>
			);
		});

		const headerErrorMessages = Array.isArray(headerErrors)
			? headerErrors.map((error, i) => (
				<li key={`header-${i}`}>
					<span className="import-header-error">{error.message}</span>
				</li>
			))
			: [];

		return [...headerErrorMessages, ...rowErrors];
	};

	const getImportMessages = (rows = []) =>
		rows.map((row, i) => {
			const hasErrors = row.errors && row.errors.length > 0;
			if (!hasErrors && row.message) {
				return (
					<li key={i}>
						<span className="import-count">{row.message}</span>
					</li>
				);
			}
			return null;
		});

	return (
		<Layout className="page-imports">
			<div className="heading">
				<div className="heading-left">
					<h1 className="title">{t("absences.import.title")}</h1>
				</div>
			</div>

			<form key={fileInputKey} onSubmit={handleImport}>
				<ol className="steps">
					<li className="step-download">
						{t("absences.import.downloadStep")}{" "}
						<span className="link" onClick={handleDownloadTemplate}>
							{t("absences.import.downloadTemplate")}
						</span>{" "}
						{t("absences.import.downloadSuffix")}
					</li>
					<li className="step-upload">
						<div className="step-upload-content">
							{t("absences.import.uploadStep")}
							<FormControl
								type="file"
								onChange={(e) => setSelectedFile(e.target.files[0])}
								className="form-control"
							/>
						</div>
					</li>
					<li className="step-config">
						<div className="step-config-content">
							{t("absences.import.configStep")}
							<FormGroup
								className="form-group form-group-flex form-row-switch form-group-main"
								controlId="ignore_errors"
							>
								<FormLabel>{t("absences.import.ignoreErrors")}</FormLabel>
								<Switch
									controlId="ignore_errors"
									isOn={ignoreErrors}
									handleToggle={handleSwitch}
								/>
							</FormGroup>
						</div>
					</li>
				</ol>
				<SubmitButton
					type="submit"
					isLoading={isLoading}
					text={t("absences.import.submit")}
					loadingText={t("absences.import.submitting")}
				/>
			</form>

			{errors.message && <p className="error-message">{errors.message}</p>}

			{!ignoreErrors && importErrors.length > 0 && (
				<div className="import-list import-errors">
					<p className="import-list-text">
						{t("absences.import.errorsFound")}
					</p>
					<ul className="import-rows">
						{getImportErrors(
							importErrors.filter((error) => error.row),
							importErrors.filter((error) => !error.row)
						)}
					</ul>
				</div>
			)}

			{importMessages.length > 0 && (
				<div className="import-list import-messages">
					<p className="import-list-text">{t("absences.import.importResult")}</p>
					<ul className="import-rows">
						{getImportMessages(importMessages)}
					</ul>
				</div>
			)}
		</Layout>
	);
}

export default AbsenceImports;
